import {
    Component,
    EventEmitter,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, ValidationErrors } from '@angular/forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import moment, { HTML5_FMT } from 'moment';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';
import { ConfigConstants } from '../../config/config-constants';
import { ClientManagerInstanceToken, ClientManagerInterface } from '../../interfaces/client-manager.interface';
import { ODataModelChangeEvent } from '../../interfaces/odata-model.interface';
import { TimeModel } from '../../models/primitive/time.model';
import { DateTimeUtilities } from '../../utilities/date-time-utilities';

@Component({
    selector: 'pace-core-datetime-input',
    templateUrl: './datetime-input.component.html',
    styleUrls: ['./datetime-input.component.scss'],
})
export class DatetimeInputComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public model: TimeModel;

    @Output()
    public modelChange = new EventEmitter<TimeModel>();

    @Input()
    public parentForm: FormArray | undefined;

    @Input()
    public ariaLabel = 'Date and Time Input';

    @Output()
    public valueChange = new EventEmitter<ODataModelChangeEvent<TimeModel>>();

    public dateTimeInputControl: FormControl;

    public faCalendar = faCalendar;

    private originalDatetime: moment.Moment;
    private previousValue: moment.Moment;

    private destroySubject = new Subject<void>();
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly translate: TranslateService,
        private readonly formBuilder: FormBuilder,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
    ) {}

    public ngOnInit(): void {
        this.previousValue = this.model.value.clone();
        this.originalDatetime = this.model.value.clone();

        const dateTimeValidator = (control: AbstractControl): ValidationErrors | null => this.checkValidity(control);
        this.dateTimeInputControl = this.formBuilder.control(
            {
                value: this.model.value.isValid() ? this.model.value.format(HTML5_FMT.DATETIME_LOCAL) : undefined,
                disabled: this.model.options.readonly,
            },
            dateTimeValidator,
        );

        this.dateTimeInputControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                tap((newVal: string) => {
                    if (this.dateTimeInputControl.errors) {
                        if (newVal === '') {
                            this.dateTimeInputControl.setValue(this.originalDatetime.format(HTML5_FMT.DATETIME_LOCAL));
                        }
                    } else {
                        const oldValue = this.model.value.clone();

                        this.model.value = DateTimeUtilities.setDateTimeHtml5(this.model.value, newVal);
                        // create new instance to trigger change detection for OnChanges
                        // and to get rid off DoCheck lifecycle hook
                        this.modelChange.emit(this.model);
                        this.valueChange.emit({ oldValue: oldValue, newValue: this.model.value });
                    }
                }),
                takeUntil(this.destroySubject),
            )
            .subscribe();

        // The actual control needs to be added asynchronously to avoid an ExpressionChangedAfterItHasBeenCheckedError
        // See also https://stackoverflow.com/questions/45661010/dynamic-nested-reactive-form-expressionchangedafterithasbeencheckederror
        if (this.parentForm) {
            const parent = this.parentForm;
            Promise.resolve(undefined)
                .then(() => {
                    parent.push(this.dateTimeInputControl);
                })
                .catch((error: Error) =>
                    this.log.error(
                        'Error in DatetimeInputComponent -> ngOnInit -> parentForm.push: ',
                        JSON.stringify(error),
                    ),
                );
        }
    }

    public ngOnChanges(changesObj: SimpleChanges): void {
        if (changesObj && changesObj.model && this.dateTimeInputControl) {
            if (!this.previousValue || (this.model.value.isValid() && !this.previousValue.isSame(this.model.value))) {
                this.previousValue = this.model.value.clone();
                this.dateTimeInputControl.setValue(this.model.value.format(HTML5_FMT.DATETIME_LOCAL));
            }
            if (this.model.options.readonly && this.dateTimeInputControl.enabled) {
                this.dateTimeInputControl.disable({ onlySelf: true, emitEvent: false });
            }
            if (!this.model.options.readonly && this.dateTimeInputControl.disabled) {
                this.dateTimeInputControl.enable({ onlySelf: true, emitEvent: false });
            }

            this.model = <TimeModel>changesObj.model.currentValue;
            this.dateTimeInputControl.setValue(this.model.value.format(HTML5_FMT.DATETIME_LOCAL));
        }
    }

    public ngOnDestroy(): void {
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex((control) => control === this.dateTimeInputControl);
            this.parentForm.removeAt(index);
        }
        this.destroySubject.next();
        this.destroySubject.complete();
    }

    public checkValidity(control: AbstractControl): ValidationErrors | null {
        // the control value is a string which contains a moment date in the format HTML5_FMT.DATETIME_LOCAL
        // we need to create from this string a date to be able to check the validity
        const potentialDateTime = moment(<string>control.value);

        const formatPattern = this.clientManager.getDateTimeFormat();
        const errors: ValidationErrors = {};

        if (!control.value) {
            errors.required = { required: 'Date and time are not selected' };
        }
        if (potentialDateTime.isBefore(this.model.options.minValue)) {
            errors.minValue = {
                text: `${this.translate.instant('TIME_INPUT_SELECT_TIME_RANGE_TEXT')} ${this.translate.instant(
                    'TIME_INPUT_VALID_TIME_RANGE_FROM_TEXT',
                    {
                        minTime: this.model.options.minValue.format(formatPattern),
                    },
                )}`,
            };
        }
        if (potentialDateTime.isAfter(this.model.options.maxValue)) {
            errors.maxValue = {
                text: `${this.translate.instant('TIME_INPUT_SELECT_TIME_RANGE_TEXT')}
                    ${this.translate.instant('TIME_INPUT_VALID_TIME_RANGE_UPTO_TEXT', {
                        maxTime: this.model.options.maxValue.format(formatPattern),
                    })}`,
            };
        }

        return Object.keys(errors).length > 0 ? errors : null;
    }
}
